/* eslint-disable no-restricted-syntax */
import flat from 'flat'
import { createGlobalStyle, css } from 'styled-components'
import { normalize } from 'styled-normalize'

import {
  ThemeColor,
  ThemeFontFamily,
  ThemeFontWeight,
  ThemeInterface,
  defaultTheme,
} from './theme'
import { space } from './utils'

const generateCSSVariables = (theme: ThemeInterface) => {
  let colors = ''
  let fonts = ''
  const fontFlat: { [key: string]: string } = flat(theme.font, {
    delimiter: '-',
  })

  for (const [key, value] of Object.entries(theme.color)) {
    colors += `--color-${key}: ${value};`
  }
  for (const [key, value] of Object.entries(fontFlat)) {
    fonts += `--font-${key}: ${value};`
  }
  return `${colors};${fonts}`
}

export const color = (themeColor: ThemeColor) => css`var(--color-${themeColor})`
export const font = (themeFont: ThemeFontFamily) =>
  css`var(--font-family-${themeFont})`
export const fontWeight = (themeFontWeight: ThemeFontWeight) =>
  css`var(--font-weight-${themeFontWeight})`

export const SubheadlineStyle = css`
  color: ${color('secondaryHeadingText')};
  font-family: ${font('base')};
  font-size: 1.1765rem;
  font-weight: ${fontWeight('bold')};
  line-height: 1.2;
  margin-bottom: ${space(1)};
  &.light {
    color: ${color('primaryText')};
  }
  &.uppercase {
    text-transform: uppercase;
  }
`

export const HeadlineStyle = css`
  color: ${color('primaryHeadingText')};
  font-family: ${font('extended')};
  font-size: 1.33333rem;
  font-weight: ${fontWeight('bold')};
  line-height: 1.25;
  &.light {
    color: ${color('primaryText')};
  }
  &.uppercase {
    text-transform: uppercase;
  }
`

export const HeadingStyle = css`
  color: ${color('primaryHeadingText')};
  font-family: ${font('extended')};
  font-size: 1.47rem;
  font-weight: ${fontWeight('bold')};
  letter-spacing: 0.05em;
  line-height: 1.2;
  text-transform: uppercase;

  @media only screen and (min-width: 960px) {
    font-size: 1.8235rem;
  }
`

export const CaptionStyle = css`
  color: ${color('secondaryHeadingText')};
  font-family: ${font('base')};
  font-size: 0.823rem;
  font-weight: ${fontWeight('bold')};
  line-height: 1.5;

  &.light {
    color: ${color('primaryText')};
  }
  &.uppercase {
    text-transform: uppercase;
  }
`

export const contentStyles = css`
  h2 {
    ${HeadlineStyle};
  }
  h3 {
    ${SubheadlineStyle};
  }
  p + h2,
  p + h3 {
    margin-top: ${space(5)};
  }
`

const loaderStyles = css`
  @keyframes loading-bar {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100vw);
    }
  }

  background: ${color('loaderBackground')};
  border-radius: 4px;
  display: block;
  height: 4px;
  margin-top: 6px;
  overflow: hidden;
  width: 100%;

  &:after {
    animation: loading-bar 2s infinite ease-in-out;
    background: ${color('interactiveHover')};
    border-radius: 4px;
    content: '';
    display: block;
    height: 4px;
    width: 60%;
  }
`

const spinnerStyles = css`
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  display: block;

  &:after {
    animation: spin 1500ms infinite linear;
    border: ${space(1)} solid ${color('interactiveSecondary')};
    border-left: ${space(1)} solid ${color('interactive')};
    border-radius: 40px;
    content: '';
    display: block;
    height: 40px;
    width: 40px;
    will-change: transform;
  }
`

export const GlobalStyle = createGlobalStyle`
  ${normalize};

  ${css`
    :root {
      ${({ theme }) => generateCSSVariables(theme || defaultTheme)}
    }
  `}

  #root {
    margin: 0 auto;
  }

  *, *:before, *:after {
    box-sizing: border-box;
    user-select:none;
  }

  html, body{
    height: 100vh;
    height: var(--vh);
    text-size-adjust: none;
  }

  html {
    font-size: 106.25%;
  }
  body {
    /* scrollbar-gutter: stable; */
    overflow-y: scroll;
  }

  .animation--overflow--hidden {
    overflow-x: hidden;
  }

  .overflow--hidden{
    overflow:hidden;
  }

  *:focus-visible{
    outline: 2px dotted ${color('background')} ;
  }

  figure, fieldset {
    padding:0;
    margin:0;
  }
  fieldset{
    border: 0;
  }
  
  button {
    cursor: pointer;
    background:none;
    border:0;
  }
  
  li {
    margin:0 0 1rem 0;
  }

  hr {
    height:0;
    margin: 1rem 0;
    width:100%;
    border-bottom:2px solid ${color('primaryText')};
  }

  body { 
    font-family: ${font('base')};
    font-weight: ${fontWeight('base')};
    background: ${color('background')};
    color: ${color('primaryText')};
    letter-spacing: 0.05rem;
    line-height: 1.5;
    > svg{
      visibility: hidden;
      height:0;
      width:0;
      display:none;
    }
  }

  p {
    line-height: 1.5;
    margin: 0 0 1rem 0;
  }

  a {
    color: ${color('interactive')};
    text-decoration: none;
    // for outlines
    border-radius: ${space(1)};
  }

  ul, ol {
    margin: 0 0 1rem 0;
    padding: 0 0 0 1rem;
  }
  h1,.h1,h2,.h2,h3,.h3, h4, .h4, h5, .h5, h6, .h6{
    margin:0;
  }
  h4, .h4, h5, .h5, h6, .h6 {
    margin-bottom:${space(2)};
  }

  h1,.h1 {
    font-size: 1.8235rem;
    font-family: ${font('extended')};
    font-weight: ${fontWeight('bold')};
    line-height: 1.2;
    letter-spacing: 0.05em; 
    color: ${color('primaryText')};
    text-transform: uppercase;
  } 
  h2,.h2 {
    font-size: 1.47rem;
    font-family: ${font('extended')};
    font-weight: ${fontWeight('bold')};
    line-height: 1.2;
    letter-spacing: 0.05em; 
    color: ${color('primaryText')};
    margin-bottom: 1rem;
  }
  h3,.h3 {
    font-size: 1.1765rem;
    font-family: ${font('extended')};
    font-weight: ${fontWeight('bold')};
    color: ${color('primaryText')};
    line-height: 1.2;
    letter-spacing: 0.06em;
    margin-bottom:${space(3)};

  }
  .heading {
    ${HeadingStyle}
  }
  .headline {
    ${HeadlineStyle}
  }
  .subheadline {
    ${SubheadlineStyle}
  }
  .caption {
    ${CaptionStyle}
  }

  .no-js-message{
    text-align:center;
    padding:20px;
    background: salmon;
    margin: 0 20px;
    border-radius: 4px;
    border: 1px solid salmon;
    box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.08);
  }

  // Spinner Loader 
  .loading {
    ${spinnerStyles};
  }

  // Loading Bar
  .loader {
    ${loaderStyles};
  }
  // Loading Screen
  .loading-screen-wrap {
    position:relative;
    z-index: 100;
  }
  .loading-screen {
    position: absolute;
    z-index: 200;
    background-color: ${color('primaryBackground')};
    background-image: url('./assets/splash.jpg');
    background-position: top center;
    background-repeat: no-repeat;
    background-size: cover;
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    will-change: opacity;
    z-index: 200;
    display:flex;
    flex-direction: column;
    padding: ${space(15)} ${space(8)};

    &:before {
      content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        right: 0;
        top: 40%;
        left:0;
        bottom: 0;
        left: 0;
        z-index: -1;
        background: linear-gradient(
          0deg,
          rgba(0, 0, 0, 1) 0%,
          rgba(0, 0, 0, 0) 100%
        );
    }
    img {
      margin: 0 auto;
      max-width: 340px;
      width:100%;
    }
    > div {
      margin-top: auto;
    }
    span {
      font-family: ${font('extended')};
      font-size: 0.823rem;
      font-weight: ${fontWeight('bold')};
      letter-spacing: 0.06em;
      line-height: 1.2;
      text-transform: uppercase;
      color: ${color('primaryText')};
    }
    h2 {
      ${HeadingStyle};
      font-size: 1.8235rem;
      font-family: ${font('extended')};
      word-spacing: 600px;
      
    }

    @media only screen and (min-width: 650px) {
      background-image: none;
      background-image: none;
      justify-content: center;
      align-items: center;

      h2 { display: none; }

      > div {
        margin-top: ${space(5)};
        max-width: 650px;
        width: 100%;
      }
    }
  }

  // Image placeholder/loader
  @keyframes pulse {
      0% {
        opacity:0.1;
      }
      
      50% {
        opacity:0.15;
      }
      
      100% {
        opacity:0.1;
      }
    }
  
  .pageLoad,
  .image-placeholder {
    background-image: url("data:image/png;base64,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");
    background-repeat: no-repeat;
    background-size: 100px;
    background-position: center;
    will-change: opacity;
    animation: pulse 1500ms infinite ease-in-out;
  }

  .pageLoad {
    width: 100%;
    min-height: var(--vh, 100%);
  }

  .image-placeholder {
    /* min-height: 100%; */
    background-color:rgba(255, 255, 255, 0.1);
  }

  .image-placeholder.loaded {
    opacity: 1;
    /* min-height: 0; */
    animation: none;
    background-image: none;
    background-color: unset;
  }

  #modal-root {
    position: relative;
    z-index: 100;
  }
`
